import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";

/**
 * The candidacy deadline a broker sets when inviting a candidate to apply
 * (DEV-3531). The broker picks a calendar day; api-next stores the end of that
 * day in Brussels and sends it back as an ISO string.
 */

/** How many days after today the date field starts on. */
export const CANDIDACY_DEADLINE_DEFAULT_DAYS = 3;

/** The form field that carries the deadline in the candidate-tenant modal. */
export const CANDIDACY_DEADLINE_FIELD = "candidacyDeadline";

/** The banner under the date that shows the line the mail will carry. */
export const CANDIDACY_DEADLINE_PREVIEW_FIELD = "candidacyDeadlinePreview";

/**
 * The sentence serverless adds to the invitation, under the broker's text.
 * The preview reads the very same key, so the two cannot drift apart.
 */
export const CANDIDACY_DEADLINE_MAIL_KEY =
  "email.rentmatch_candidate_tenant_invite.deadline";

/**
 * The Intl locale per mail locale, as serverless formats the day in the mail
 * (visitCalendar.utils, INTL_LOCALES).
 */
const INTL_LOCALE: { [locale: string]: string } = {
  [ELocale.NL]: "nl-BE",
  [ELocale.FR]: "fr-BE",
  [ELocale.EN]: "en-GB",
};

const BRUSSELS_TIME_ZONE = "Europe/Brussels";

const CALENDAR_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Today's calendar day in Brussels, plus `addDays`, as local midnight of that
 * day. api-next refuses a deadline before today in Brussels, so the browser's
 * own day (a laptop abroad, or the minutes around midnight) must not decide
 * it. Local midnight because the picker and `toCandidacyDeadlinePayload` read
 * a Date's local fields.
 */
const getBrusselsDay = (now: Date, addDays: number) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BRUSSELS_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value);

  return new Date(
    getPart("year"),
    getPart("month") - 1,
    getPart("day") + addDays,
  );
};

/** Today in Brussels: the earliest day the picker offers. */
export const getStartOfToday = (now: Date = new Date()) =>
  getBrusselsDay(now, 0);

/** Today in Brussels plus three days. */
export const getDefaultCandidacyDeadline = (now: Date = new Date()) =>
  getBrusselsDay(now, CANDIDACY_DEADLINE_DEFAULT_DAYS);

/**
 * What `PUT /applications/:id/step` expects: the picked calendar day as
 * 'YYYY-MM-DD', or null when the broker cleared the field.
 *
 * A picked Date is read in local time, because that is the day the broker saw
 * in the picker. Going through `toISOString` would turn midnight in Brussels
 * into the previous day in UTC.
 */
export const toCandidacyDeadlinePayload = (value: unknown): string | null => {
  if (value == null || value === "") {
    return null;
  }

  if (typeof value === "string" && CALENDAR_DAY_PATTERN.test(value)) {
    return value;
  }

  const date =
    value instanceof Date ? value : new Date(value as string | number);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const year = `${date.getFullYear()}`;
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * The deadline as day/month in Brussels, whatever the browser's time zone, so
 * the end of the 24th never reads as the 25th on a laptop abroad.
 */
export const formatCandidacyDeadlineDay = (deadline: Date) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BRUSSELS_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
  }).formatToParts(deadline);

  const day = parts.find(part => part.type === "day")?.value;
  const month = parts.find(part => part.type === "month")?.value;

  return `${day}/${month}`;
};

export interface ICandidacyDeadlineLabel {
  /** Past the end of the deadline day. */
  isPassed: boolean;
  /** The deadline day as dd/MM. */
  date: string;
}

/**
 * The step pill for a candidate who was invited to apply with a deadline.
 *
 * Only on the candidate-tenant step and only while no candidacy was submitted:
 * a submitted one keeps its green "submitted" pill. Until the end of the
 * deadline day it reads "Deadline 24/10", after that "Expired 24/10"; the
 * caller picks the copy, so the i18n key check sees both keys.
 * Undefined means the row keeps whatever label it had.
 */
export const getCandidacyDeadlineLabel = (params: {
  step: EIntakeStep;
  showsSubmittedPill: boolean;
  candidacyDeadline?: string | null;
  now?: Date;
}): ICandidacyDeadlineLabel | undefined => {
  const {
    step,
    showsSubmittedPill,
    candidacyDeadline,
    now = new Date(),
  } = params;

  if (
    step !== EIntakeStep.candidateTenant ||
    showsSubmittedPill ||
    !candidacyDeadline
  ) {
    return undefined;
  }

  const deadline = new Date(candidacyDeadline);

  if (Number.isNaN(deadline.getTime())) {
    return undefined;
  }

  return {
    isPassed: now.getTime() > deadline.getTime(),
    date: formatCandidacyDeadlineDay(deadline),
  };
};

/**
 * The picked day as the mail writes it: weekday, day and month in the reader's
 * language, "Friday 24 October". Noon UTC of that calendar day, read in
 * Brussels, is that same day whatever the browser's zone.
 */
export const formatCandidacyDeadlineForMail = (
  value: unknown,
  locale: string,
): string | undefined => {
  const day = toCandidacyDeadlinePayload(value);
  if (!day) {
    return undefined;
  }

  return new Intl.DateTimeFormat(
    INTL_LOCALE[locale] || INTL_LOCALE[ELocale.NL],
    {
      timeZone: BRUSSELS_TIME_ZONE,
      weekday: "long",
      day: "numeric",
      month: "long",
    },
  ).format(new Date(`${day}T12:00:00Z`));
};

/**
 * The deadline line of the invitation, once per language in the selection, as
 * the candidates will read it, in Markdown for the banner. Empty without a
 * deadline: then the mail carries no such line either.
 *
 * The copy puts the date in <b> for the HTML mail; the banner renders Markdown,
 * which shows raw HTML as nothing at all, so the tags become ** here.
 */
export const getCandidacyDeadlineMailPreview = (
  value: unknown,
  locales: string[],
): string => {
  return locales
    .map(locale => {
      const deadline = formatCandidacyDeadlineForMail(value, locale);
      if (!deadline) {
        return undefined;
      }

      return getLocalizedText(
        CANDIDACY_DEADLINE_MAIL_KEY,
        { deadline },
        0,
        locale as ELocale,
      ).replace(/<\/?b>/g, "**");
    })
    .filter(Boolean)
    .join("\n\n");
};
